import React from 'react';
import Tool, { type ToolArgs } from '../../../tools/Tool';
import { ReactEditor } from 'slate-react';
import { Transforms, type Element } from 'slate';
import styles from '../components/ImageComponentPlugin.module.css';
import classnames from 'classnames';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import positionBlockSvg from '../../../assets/icons/positionBlock.svg';
import positionInlineSvg from '../../../assets/icons/positionInline.svg';
import { isMediaElement } from '../../../types/guards';
import MediaCommon from '../MediaCommon';

interface ComponentWrapperProps {
  attributes: Record<string, unknown>;
  element: Element;
  children?: React.ReactNode;
}

const withImagePosition = <P extends ComponentWrapperProps>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const ComponentWrapper: React.FC<P> = (props) => {
    const { attributes, children, element } = props;

    if (!isMediaElement(element)) return null;

    return (
      <span
        {...attributes}
        className={classnames({
          [styles.imageInline]: element.media_position === 'inline',
          [styles.imageBlock]: element.media_position !== 'inline',
        })}
      >
        <Component {...props} />
        {children}
      </span>
    );
  };
  return ComponentWrapper;
};

class ToolPositionBlock extends Tool {
  static toolName = 'Center';

  static action({ editor, element }: ToolArgs): void {
    if (!element) return;

    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      { media_position: 'block' },
      {
        at: path,
      }
    );

    localStorage.setItem(MediaCommon.mediaPositionPrefKey, 'block');
  }

  static isActive({ element }: ToolArgs): boolean {
    return Boolean(
      element && isMediaElement(element) && element.media_position === 'block'
    );
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(positionBlockSvg);
  }
}

class ToolPositionInline extends Tool {
  static toolName = 'Inline';

  static action({ editor, element }: ToolArgs): void {
    if (!element) return;

    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      {
        media_position: 'inline',
      },
      {
        at: path,
      }
    );

    localStorage.setItem(MediaCommon.mediaPositionPrefKey, 'inline');
  }

  static isActive({ element }: ToolArgs): boolean {
    return Boolean(
      element && isMediaElement(element) && element.media_position === 'inline'
    );
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(positionInlineSvg);
  }
}

export { withImagePosition, ToolPositionInline, ToolPositionBlock };
